import { Box } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { SiteImage } from "../siteKit/media/SiteImage";
import {
  siteFileUrl,
  siteImageUrl,
  siteVideoPosterUrl,
  siteVideoUrl,
} from "../siteKit/media/siteMedia";
import type { SceneContext } from "./SceneSection";
import { SCENE_STYLES } from "./sceneStyles";
import type {
  TerrainPreset,
  TerrainSceneController,
  TerrainStepState,
} from "./terrainScene";

/** 장면 데이터는 화면 이만큼 앞에서 받기 시작한다 */
const SCENE_ROOT_MARGIN = "900px";
const TEXTURE_WIDTH = 1900;
const COLORS_WIDTH = 960;
const WORK_SITE_VIDEO = "ws3d_sim" as const;
const WORK_SITE_VIDEO_WIDTH = 1280;
const WORK_SITE_POSTER_WIDTH = 960;
const CHEVRON_PATH = "M4 6l4 4 4-4";

/** 단계 모드별 미리 정해진 값 — 장면 모듈을 받기 전에도 대체 그림·HUD 를 정한다(장면 모듈과 같은 값) */
const PRESET_MODES: Record<
  TerrainPreset,
  ReadonlyArray<TerrainStepState["mode"]>
> = {
  view: ["2d", "3d", "4d"],
  layers: ["3d", "3d", "3d", "3d", "3d", "3d", "ws"],
};

export const TERRAIN_DURATIONS: Record<TerrainPreset, readonly number[]> = {
  view: [2400, 3400, 5600],
  layers: [3000, 3200, 3200, 3400, 3800, 4200, 19800],
};

function DateChip({
  text,
  sx,
  chipRef,
}: {
  text: string;
  sx: object;
  chipRef?: (element: HTMLSpanElement | null) => void;
}) {
  return (
    <Box
      component="span"
      ref={chipRef}
      aria-hidden="true"
      sx={[SCENE_STYLES.date, sx]}
    >
      <span data-text="">{text}</span>
      <i data-divider="" />
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <path
          d={CHEVRON_PATH}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
}

/**
 * 지형 장면 칸 내용(2D·3D·4D / 레이어): 대체 그림·모드 표시·날짜 칩·(레이어) 영상 기록 카드·작업현황 영상.
 * 장면 엔진은 화면 가까이 오면 받아 붙이고, 단계가 바뀔 때마다 그 단계 상태로 넘긴다.
 */
export function TerrainSceneContent({
  context,
  preset,
}: {
  context: SceneContext;
  preset: TerrainPreset;
}) {
  const { t } = useTranslation("landing");
  const {
    rootRef,
    step,
    previousStep,
    snap,
    active,
    activeRef,
    reducedMotion,
  } = context;
  const fallbackRef = useRef<HTMLImageElement | null>(null);
  const dateRef = useRef<HTMLSpanElement | null>(null);
  const paneDateRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const geologCardRef = useRef<HTMLImageElement | null>(null);
  const workSiteRef = useRef<HTMLDivElement | null>(null);
  const workVideoRef = useRef<HTMLVideoElement | null>(null);
  const sceneRef = useRef<TerrainSceneController | null>(null);
  const moduleRef = useRef<typeof import("./terrainScene") | null>(null);
  const modes = PRESET_MODES[preset];
  const mode = modes[step];
  const isLayers = preset === "layers";
  const paneDates = t("site.scenes.dates.panes", {
    returnObjects: true,
  }) as readonly string[];
  const issues = t("site.scenes.layers.issues", {
    returnObjects: true,
  }) as readonly string[];

  // 화면 가까이 오면 장면 엔진·데이터를 받아 붙인다
  useEffect(() => {
    const root = rootRef.current;
    const fallback = fallbackRef.current;
    if (!root || !fallback) return undefined;
    let disposed = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        void (async () => {
          const [assetsModule, terrainModule] = await Promise.all([
            import("./sceneAssets"),
            import("./terrainScene"),
          ]);
          if (disposed || !assetsModule.canUseWebGL()) return;
          const { loadImage, loadTextureFactory } = assetsModule;
          const steps = terrainModule.TERRAIN_PRESETS[preset];
          const needsColors = steps.some((item) => "pc" in item && item.pc);
          const [now, past, heightNow, heightPast, mask, small, pin] =
            await Promise.all([
              loadTextureFactory(
                siteFileUrl("data/ortho_now.ktx2"),
                siteImageUrl("ortho_now", TEXTURE_WIDTH)
              ),
              loadTextureFactory(
                siteFileUrl("data/ortho_past.ktx2"),
                siteImageUrl("ortho_past", TEXTURE_WIDTH)
              ),
              loadImage(siteFileUrl("data/h_now.png")),
              loadImage(siteFileUrl("data/h_past.png")),
              loadImage(siteFileUrl("data/tmask.png")),
              needsColors
                ? loadImage(siteImageUrl("ortho_now", COLORS_WIDTH))
                : Promise.resolve(null),
              isLayers
                ? loadImage(siteFileUrl("ui/marker-sharp.png"))
                : Promise.resolve(null),
            ]);
          if (disposed) return;
          const scene = await terrainModule.createTerrainScene({
            dom: {
              root,
              fallback,
              date: dateRef.current,
              paneDates: paneDateRefs.current.filter(
                (element): element is HTMLSpanElement => Boolean(element)
              ),
              geologCard: geologCardRef.current,
            },
            assets: { now, past, heightNow, heightPast, mask, small, pin },
            steps,
            labels: {
              wallBim: t("site.scenes.layers.wallBim"),
              waterPipe: t("site.scenes.layers.waterPipe"),
              stormPipe: t("site.scenes.layers.stormPipe"),
              issues,
            },
            isActive: () => activeRef.current,
            reducedMotion,
          });
          if (disposed) {
            scene.dispose();
            return;
          }
          moduleRef.current = terrainModule;
          sceneRef.current = scene;
          const current = terrainModule.terrainStateOf(steps[stepRef.current]);
          scene.update(current, { ...current, mode: "2d" }, true);
        })().catch(() => {
          sceneRef.current = null;
        });
      },
      { rootMargin: SCENE_ROOT_MARGIN }
    );
    observer.observe(root);
    return () => {
      disposed = true;
      observer.disconnect();
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
    // 장면은 한 번만 만든다 — 단계·문구 변화는 아래 효과가 넘긴다
  }, [preset]);

  const stepRef = useRef(step);
  stepRef.current = step;

  // 단계가 바뀌면 장면에 넘기고, 작업현황 단계면 영상을 처음부터
  useEffect(() => {
    const terrainModule = moduleRef.current;
    const scene = sceneRef.current;
    if (terrainModule && scene) {
      const steps = terrainModule.TERRAIN_PRESETS[preset];
      scene.update(
        terrainModule.terrainStateOf(steps[step]),
        terrainModule.terrainStateOf(steps[previousStep]),
        snap
      );
    }
  }, [preset, step, previousStep, snap]);

  const playsWorkVideo = mode === "ws" && active && !reducedMotion;
  // 작업현황 영상: 이 단계이거나 다음이 이 단계면 포스터·영상을 미리 받고, 이 단계에서만 처음부터 재생
  useEffect(() => {
    const video = workVideoRef.current;
    if (!video) return;
    const nextMode = modes[(step + 1) % modes.length];
    const needsWorkVideo = mode === "ws" || nextMode === "ws";
    if (needsWorkVideo && !video.getAttribute("src")) {
      video.poster = siteVideoPosterUrl(
        WORK_SITE_VIDEO,
        WORK_SITE_POSTER_WIDTH
      );
      video.preload = "auto";
      video.src = siteVideoUrl(WORK_SITE_VIDEO, WORK_SITE_VIDEO_WIDTH);
    }
    workSiteRef.current?.toggleAttribute("data-on", mode === "ws");
    if (playsWorkVideo) {
      try {
        video.currentTime = 0;
      } catch {
        // 메타데이터 전이면 처음부터 재생된다
      }
      video.play().catch(() => {});
    } else {
      video.pause();
    }
    // 장면 단계 전환마다 처음부터 — 보이기 전환은 아래 효과가 이어 재생한다
  }, [step, mode]);

  useEffect(() => {
    sceneRef.current?.wake();
    const video = workVideoRef.current;
    if (!video) return;
    if (playsWorkVideo) video.play().catch(() => {});
    else video.pause();
  }, [active, mode, reducedMotion]);

  const fallbackIs3d = mode === "3d" || mode === "ws";
  return (
    <>
      <SiteImage
        ref={fallbackRef}
        name={fallbackIs3d ? "ortho3d" : "ortho_now"}
        sizes="(max-width: 900px) 50vw, 440px"
        alt=""
        placeholder="none"
        sx={SCENE_STYLES.fallback}
      />
      {isLayers ? (
        <SiteImage
          ref={geologCardRef}
          name="geolog_vcard"
          sizes="190px"
          alt=""
          placeholder="none"
          sx={SCENE_STYLES.geologCard}
        />
      ) : (
        <Box aria-hidden="true" sx={SCENE_STYLES.hud}>
          <Box component="span" sx={SCENE_STYLES.mode}>
            {(["2d", "3d", "4d"] as const).map((item) => (
              <b key={item} data-on={item === mode ? "" : undefined}>
                {item.toUpperCase()}
              </b>
            ))}
          </Box>
        </Box>
      )}
      <DateChip
        text={t("site.scenes.dates.main")}
        sx={SCENE_STYLES.mainDate}
        chipRef={(element) => {
          dateRef.current = element;
        }}
      />
      {!isLayers &&
        paneDates.map((text, index) => (
          <DateChip
            key={text}
            text={text}
            sx={SCENE_STYLES.paneDate}
            chipRef={(element) => {
              paneDateRefs.current[index] = element;
            }}
          />
        ))}
      {isLayers && (
        <Box ref={workSiteRef} aria-hidden="true" sx={SCENE_STYLES.workSite}>
          <video ref={workVideoRef} muted playsInline loop preload="none" />
        </Box>
      )}
    </>
  );
}
